/**
 * Heikle Schritte mit dem Code aus der App bestätigen (Ausbau H, 28.10.2026).
 *
 * Website anlegen, Domain verbinden und der erste Livegang einer neuen Website
 * verlangen im Server eine frische Bestätigung (`bestaetigung_noetig`, 403).
 * Statt in jedem Dialog eine eigene Code-Abfrage zu bauen, geht der Aufruf über
 * `mit()`: Kommt die Antwort, öffnet sich einmal der Dialog
 * (`BestaetigungModal`), und nach dem Code läuft derselbe Aufruf noch einmal.
 * Zehn Minuten lang fragt der Server danach nicht wieder.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { ApiError } from '@/lib/api'

export class BestaetigungAbgebrochen extends Error {
  constructor() {
    super('bestaetigung_abgebrochen')
    this.name = 'BestaetigungAbgebrochen'
  }
}

/** Verlangt der Server für diesen Aufruf eine Bestätigung mit Code? */
export function brauchtBestaetigung(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403
    && (e.body as { error?: unknown } | null)?.error === 'bestaetigung_noetig'
}

export const useBestaetigungStore = defineStore('bestaetigung', () => {
  const offen = ref(false)
  let warten: { ja: () => void; nein: (e: Error) => void } | null = null

  /** Den Dialog öffnen; erfüllt sich, sobald der Code angenommen ist. */
  function frage(): Promise<void> {
    warten?.nein(new BestaetigungAbgebrochen())
    offen.value = true
    return new Promise<void>((ja, nein) => {
      warten = { ja, nein }
    })
  }

  function bestaetigt(): void {
    offen.value = false
    warten?.ja()
    warten = null
  }

  function abgebrochen(): void {
    offen.value = false
    warten?.nein(new BestaetigungAbgebrochen())
    warten = null
  }

  /** Einen Aufruf ausführen und bei Bedarf einmal nach dem Code fragen. */
  async function mit<T>(aufruf: () => Promise<T>): Promise<T> {
    try {
      return await aufruf()
    } catch (e) {
      if (!brauchtBestaetigung(e)) throw e
      await frage()
      return await aufruf()
    }
  }

  return { offen, frage, bestaetigt, abgebrochen, mit }
})
